import { useNavigate as useRouterNavigate } from 'react-router-dom';
import useLanguage from './useLanguage';

// ----------------------------------------------------------------------

/**
 * Language-aware navigate hook
 * Wraps React Router's useNavigate to automatically add language prefixes
 *
 * Usage:
 *   const navigate = useLanguageNavigate();
 *   navigate('/blog'); // Navigates to /en/blog or /es/blog
 *
 * Supports all navigate options:
 *   navigate('/blog', { replace: true });
 *   navigate(-1); // Go back
 *   navigate({ pathname: '/blog', search: '?tag=science' });
 *
 * @returns {Function} Language-aware navigate function
 */
export default function useLanguageNavigate() {
  const routerNavigate = useRouterNavigate();
  const { getLocalizedPath } = useLanguage();

  /**
   * Navigate with automatic language prefix
   * @param {string|number|Object} to - Destination path or navigation delta
   * @param {Object} options - Navigation options (replace, state, etc.)
   */
  const navigate = (to, options) => {
    // Handle numeric navigation (e.g., navigate(-1) for back)
    if (typeof to === 'number') {
      return routerNavigate(to);
    }

    // Handle object navigation with pathname
    if (typeof to === 'object' && to.pathname) {
      const localizedTo = {
        ...to,
        pathname: getLocalizedPath(to.pathname),
      };
      return routerNavigate(localizedTo, options);
    }

    // Handle string navigation
    if (typeof to === 'string') {
      const localizedTo = getLocalizedPath(to);
      return routerNavigate(localizedTo, options);
    }

    // Fallback to original navigate for edge cases
    return routerNavigate(to, options);
  };

  return navigate;
}
